/** Shared helpers for parsing structured API error codes from axios-style responses. */

function readStructuredError(payload: unknown):
  | {
      code?: string;
      error_code?: string;
      message?: string;
      details?: Record<string, unknown>;
    }
  | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const error = (
    payload as {
      error?: {
        code?: string;
        error_code?: string;
        message?: string;
        details?: Record<string, unknown>;
      };
    }
  ).error;
  if (!error || typeof error !== "object") return undefined;
  return error;
}

function readStructuredErrorCode(payload: unknown): string | undefined {
  const error = readStructuredError(payload);
  if (!error) return undefined;
  if (typeof error.code === "string") return error.code;
  if (typeof error.error_code === "string") return error.error_code;
  return undefined;
}

function readDirectErrorCode(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const direct = payload as { code?: unknown; error_code?: unknown };
  if (typeof direct.code === "string") return direct.code;
  if (typeof direct.error_code === "string") return direct.error_code;
  return undefined;
}

/** Parse structured error message from API response body. */
export function extractApiErrorMessageFromResponseData(
  data: unknown
): string | undefined {
  if (!data || typeof data !== "object" || data === null) return undefined;
  const top = data as { detail?: unknown; error?: unknown; message?: unknown };
  // Prefer canonical ``error.message`` (Phase 3 envelope), then nested/legacy detail.
  const message =
    readStructuredError(top)?.message ??
    readStructuredError(top.detail)?.message ??
    (top.detail && typeof top.detail === "object"
      ? (top.detail as { message?: unknown }).message
      : undefined) ??
    (typeof top.detail === "string" ? top.detail : undefined) ??
    (typeof top.message === "string" ? top.message : undefined);
  return typeof message === "string" && message.trim()
    ? message.trim()
    : undefined;
}

/** Parse structured error code from API response body (`error.code` or legacy `error.error_code`). */
export function extractApiErrorCodeFromResponseData(
  data: unknown
): string | undefined {
  if (!data || typeof data !== "object" || data === null) return undefined;
  const top = data as { detail?: unknown; error?: unknown };
  return (
    readStructuredErrorCode(top.detail) ??
    readDirectErrorCode(top.detail) ??
    readStructuredErrorCode(top) ??
    readDirectErrorCode(top)
  );
}

/** Parse structured error code from an axios-style API error. */
export function apiErrorCodeFromUnknown(err: unknown): string | undefined {
  if (!err || typeof err !== "object" || !("response" in err)) return undefined;
  const response = (err as { response?: { data?: unknown; status?: number } })
    .response;
  return extractApiErrorCodeFromResponseData(response?.data);
}

export function apiErrorStatusFromUnknown(err: unknown): number | undefined {
  if (!err || typeof err !== "object" || !("response" in err)) return undefined;
  return (err as { response?: { status?: number } }).response?.status;
}

/** Parse structured error details from API response body. */
export function extractApiErrorDetailsFromResponseData(
  data: unknown
): Record<string, unknown> | undefined {
  if (!data || typeof data !== "object" || data === null) return undefined;
  const top = data as { detail?: unknown; error?: unknown };
  const details =
    readStructuredError(top.detail)?.details ??
    readStructuredError(top)?.details;
  return details && typeof details === "object" ? details : undefined;
}

/** Parse structured error details from an axios-style API error. */
export function apiErrorDetailsFromUnknown(
  err: unknown
): Record<string, unknown> | undefined {
  if (!err || typeof err !== "object" || !("response" in err)) return undefined;
  const response = (err as { response?: { data?: unknown } }).response;
  return extractApiErrorDetailsFromResponseData(response?.data);
}

/** Parse structured error message from an axios-style API error. */
export function apiErrorMessageFromUnknown(err: unknown): string | undefined {
  if (!err || typeof err !== "object" || !("response" in err)) return undefined;
  const response = (err as { response?: { data?: unknown } }).response;
  return extractApiErrorMessageFromResponseData(response?.data);
}
